import React, {useState} from 'react'
import Chip from '@mui/material/Chip'
import Grid from '@mui/material/Grid'
import Stack from '@mui/material/Stack'
import {InfoItem} from '../../components/text'
import {RoundedButton} from '../../components/forms/inputs'
import useEditable from '../../hooks/useEditable'
import Path from '../../routeLinks'
import type {Alarm as AlarmType, Model, Evaluation} from '../../services/types'
import {StatusIcons, itemsSelect, MetricSelect, ThresholdInput, VisibilityToggle} from './widgets'

type SummaryProps = {
  alarm: AlarmType
  updateAlarm?: (alarm: AlarmType) => void
  allEvals?: Evaluation[]
  allModels?: Model[]
}
export const AlarmSummary = ({alarm, updateAlarm, allEvals, allModels}: SummaryProps) => {
  const [models, setModels] = useState<string[]>([...(alarm.models || [])].map((m) => m.id))
  const {Editable, onSave, onCancel, onEdit, handleChange, editable} = useEditable<AlarmType>(
    alarm,
    updateAlarm
  )

  const ModelsSelect = allModels && itemsSelect('Models', allModels)
  const updateModels = (ids: string[]) => {
    handleChange(
      'models',
      allModels?.filter((m) => ids.includes(m.id))
    )
    setModels(ids)
  }

  const removeModel = (id: string) => updateModels(models.filter((model) => model !== id))
  const ModelChip = ({id, name}: Model) => (
    <Chip
      key={id}
      label={name}
      variant="outlined"
      onDelete={editable ? () => removeModel(id) : undefined}
    />
  )

  return (
    <Stack
      component="form"
      direction={{sm: 'column', md: 'row'}}
      justifyContent="space-evenly"
      spacing={3}
    >
      <StatusIcons alarm={alarm} />

      <Grid container spacing={2}>
        <Grid item md={9} sm={12}>
          <Editable getter="name" variant="h3" label="Name" />
        </Grid>
        <Grid item md={3} sm={12}>
          <InfoItem
            title="Alarm creator"
            value={alarm?.owner?.full_name}
            to={alarm?.owner?.id && Path.user.profile(alarm?.owner?.id)}
          />
        </Grid>

        {/* #### */}
        <Grid item md={9} sm={12}>
          <Editable getter="desc" label="Description" markdown />
        </Grid>
        <Grid item sm={3} xs={12}>
          {!editable && (
            <InfoItem title="Evaluator" value={alarm?.evaluation?.owner?.full_name || '-'} />
          )}
        </Grid>

        {/* #### */}
        <Grid item xs={12} sm={6} md={3} lg={2}>
          <InfoItem title="Next predicted" value={alarm.predicted_trigger_date} />
        </Grid>
        <Grid item sm={6} xs={12} md={3} lg={3}>
          <Editable
            title="Visibility"
            getter="isPublic"
            formatter={(val: boolean) => (val ? 'public' : 'private')}
            editor={VisibilityToggle}
          />
        </Grid>

        <Grid item sm={6} xs={12} md={3} lg={2}>
          <Editable title="Metric" getter="metric" editor={MetricSelect} />
        </Grid>
        <Grid item sm={6} xs={12} md={3} lg={2}>
          <Editable title="Threshold" getter="threshold" editor={ThresholdInput} />
        </Grid>

        <Grid item xs={12} sm={6} md={6} lg={3}>
          {allEvals && (
            <Editable
              title="Evaluation"
              formatter={(id: string) => allEvals.filter((i) => i.id === id)[0]?.name}
              getter={(i: AlarmType) => i.evaluation?.id}
              setter={(val: any) => {
                if (allEvals) {
                  const selected = allEvals.filter(({id}) => id === val)[0]
                  handleChange('evaluation', selected)
                }
              }}
              editor={itemsSelect('Evaluation', allEvals)}
            />
          )}
        </Grid>

        {/* #### */}
        <Grid item xs={2} sm={6} md={6} lg={9}>
          <InfoItem title="Last trigger" value={alarm.last_trigger_date} evenSpacing />
        </Grid>

        <Grid item xs={12} sm={6} md={6} lg={3}>
          {allModels && editable && ModelsSelect && (
            <ModelsSelect value={models} multiple onChange={updateModels} />
          )}
        </Grid>
        {/* #### */}
        <Grid item xs={12}>
          {allModels && (
            <InfoItem
              title="Models"
              value={allModels.filter((m) => models.includes(m.id)).map(ModelChip)}
            />
          )}
        </Grid>

        {/* #### */}
        <Grid item xs={6} sm={3} md={6} lg={8} />
        <Grid item xs={6} sm={1} md={6} lg={8} />
        <Grid item xs={6} sm={4} md={3} lg={2}>
          {editable && <RoundedButton label="Save" onClick={onSave} />}
        </Grid>
        <Grid item xs={6} sm={4} md={3} lg={2}>
          {editable && <RoundedButton label="Cancel" onClick={onCancel} />}
          {!editable && updateAlarm && onEdit && <RoundedButton label="Edit" onClick={onEdit} />}
        </Grid>
      </Grid>
    </Stack>
  )
}

export default AlarmSummary
